// LiveStreamTile.jsx
//
// Live-stream tile for the dashboard — the bottom-right tile. Plays one Disney
// parks live stream at a time, advancing seamlessly to the next when the
// current one ends, errors, or drops off the live list, or when tapped.
//
// PLAYER: uses react-youtube directly (own audio path at volume 100,
// VIP multiview). This gives onEnded / onError JS callbacks for a clean
// handoff. If this version causes the dashboard's scroll/board to stutter on
// slow Android displays, revert to the raw-<iframe> version
// (LiveStreamTile_IFRAME_BACKUP) — that one has no YouTube JS API overhead.
//
// ─── Seamless handoff ─────────────────────────────────────────────────────
// Three triggers all converge on advanceStream():
//   1. player onEnd  (YouTube end-of-video signal)
//   2. player onError
//   3. the current stream disappears from the RTDB live list
// The player is keyed on videoId, so every switch fully remounts the
// player — no frozen last frame.
//
// ─── VIP gate ─────────────────────────────────────────────────────────────
// Renders nothing unless the device has the VIP addon.
//
// ─── Props ────────────────────────────────────────────────────────────────
//   style  (optional)  merged onto the outer tile container.

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLiveData } from "../../hooks/useLiveData";
import useGlobalStore from "../../store/global.store";
import YouTube from "react-youtube";
import ClockTile from "./ClockTile";
import {
  BLOCKED_VIDEO_IDS,
  TOP_STREAMERS,
  PRE_RECORDED_NAMES,
} from "./MultiView/multi-view.utils";

// Sorted, playable stream list — same logic as the multiview's
// getSortedThumbnails: drop blocked ids, TOP streamers first, loops last.
function getSortedStreams(streams) {
  if (!streams) return [];
  return Object.entries(streams)
    .filter(
      ([, data]) => data?.videoId && !BLOCKED_VIDEO_IDS.has(data.videoId)
    )
    .map(([name, data]) => ({
      key: name,
      streamer: data.name || name,
      videoId: data.videoId,
      title: data.title || "",
      isTop: TOP_STREAMERS.has(name),
      isLoop: PRE_RECORDED_NAMES.has(name),
    }))
    .sort((a, b) => {
      if (a.isTop !== b.isTop) return b.isTop - a.isTop;
      if (a.isLoop !== b.isLoop) return a.isLoop - b.isLoop;
      return 0;
    });
}

const LiveStreamTile = forwardRef(function LiveStreamTile({ timezone, style }, ref) {
  const hasVipAddOn = useGlobalStore((state) => state.hasVipAddOn);
  const { liveStreams, addEndedStream } = useLiveData();

  const [currentVideoId, setCurrentVideoId] = useState("");
  // Dashboard audio-source control: when audible, the tile's own player
  // unmutes at volume 100 (independent of the multiview sound store).
  const [audible, setAudible] = useState(false);
  // Playback volume 0-100. Dashboard pushes a SHARED value in here (display
  // only) via the setVolume handle, so this tile and the music tile are
  // always at identical loudness. TV never calls it -> stays at 100.
  const [volume, setVolume] = useState(100);
  const ytRef = useRef(null);

  // Apply the audible state to the live player (own audio path — does NOT
  // ride the multiview sound store, which has its own global mute/volume).
  useEffect(() => {
    const p = ytRef.current?.internalPlayer;
    if (!p) return;
    try {
      p.setVolume(volume);
      if (audible) p.unMute();
      else p.mute();
    } catch (e) {
      /* player mid-remount */
    }
  }, [audible, currentVideoId, volume]);

  // Sorted playable list, rebuilt whenever the live list changes.
  const sortedStreams = useMemo(
    () => getSortedStreams(liveStreams),
    [liveStreams]
  );

  // Ref of the latest list so advanceStream always sees current data.
  const sortedRef = useRef(sortedStreams);
  sortedRef.current = sortedStreams;

  // Advance to the next stream in the sorted list (wraps around).
  const advanceStream = useRef(() => {});
  advanceStream.current = () => {
    const list = sortedRef.current;
    if (!list.length) {
      setCurrentVideoId("");
      return;
    }
    const idx = list.findIndex((s) => s.videoId === currentVideoId);
    const nextIdx = idx < 0 ? 0 : (idx + 1) % list.length;
    setCurrentVideoId(list[nextIdx].videoId);
  };

  // Dashboard header controls (AUDIO cycler / NEW STREAM button).
  useImperativeHandle(
    ref,
    () => ({
      setMuted: (m) => setAudible(!m),
      setVolume: (v) => {
        const n = Math.min(100, Math.max(0, Number(v) || 0));
        setVolume(n);
        return { volume: n };
      },
      // Jump to a random DIFFERENT live stream.
      switchRandom: () => {
        const list = sortedRef.current.filter(
          (s) => s.videoId !== currentVideoId,
        );
        if (!list.length) return null;
        const pick = list[Math.floor(Math.random() * list.length)];
        setCurrentVideoId(pick.videoId);
        return pick;
      },
    }),
    [currentVideoId, volume],
  );

  // Pick an initial stream once the list is populated.
  useEffect(() => {
    if (!currentVideoId && sortedStreams.length) {
      setCurrentVideoId(sortedStreams[0].videoId);
    }
  }, [sortedStreams, currentVideoId]);

  // Trigger 3: the current stream dropped off the RTDB live list -> advance.
  // Only after the absence PERSISTS. The list is rewritten often, and a
  // single snapshot omitting the current id (mid-write, reconnect, prune)
  // would change currentVideoId -> the key changes -> the player fully
  // remounts. That remount is the spinner/reload hiccup.
  useEffect(() => {
    if (!currentVideoId) return;
    const stillLive = sortedStreams.some((s) => s.videoId === currentVideoId);
    if (stillLive) return;
    const t = setTimeout(() => {
      if (!sortedRef.current.some((s) => s.videoId === currentVideoId)) {
        advanceStream.current();
      }
    }, 20000);
    return () => clearTimeout(t);
  }, [sortedStreams, currentVideoId]);

  // Triggers 1 & 2: player end / error -> mark ended (drops it from
  // useLiveData) then advance to the next stream.
  const handleEnded = (videoId) => {
    if (videoId) addEndedStream(videoId);
    advanceStream.current();
  };
  const handleError = (videoId) => {
    if (videoId) addEndedStream(videoId);
    advanceStream.current();
  };

  // VIP gate — VIP devices get the live stream; everyone else gets a large
  // clock in this same tile slot.
  if (!hasVipAddOn) return <ClockTile timezone={timezone} style={style} />;

  const current = sortedStreams.find((s) => s.videoId === currentVideoId);
  const hasStream = Boolean(current);

  return (
    <div
      style={{
        // White frame — outer tile white with padding; video in a black
        // rounded inner box. Matches the prototype's framed look.
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: 20,
        overflow: "hidden",
        background: "#ffffff",
        border: "1px solid rgba(11,31,53,0.12)",
        boxShadow: "0 10px 28px rgba(11,31,53,0.16)",
        padding: 10,
        boxSizing: "border-box",
        cursor: hasStream ? "pointer" : "default",
        ...style,
      }}
      onClick={() => {
        // Tap advances to the next stream.
        if (hasStream) advanceStream.current();
      }}
    >
      {/* Inner black video box */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: 12,
          overflow: "hidden",
          background: "#0b1220",
        }}
      >
        {hasStream ? (
          <>
            {/* Player — keyed on videoId so each switch fully remounts
                (no stale last frame). isSelected=false keeps it muted.
                Over-sized + offset (-8% / 116%) so YouTube's edge chrome
                is cropped out of view. */}
            <div
              style={{
                position: "absolute",
                top: "-8%",
                left: "-8%",
                width: "116%",
                height: "116%",
              }}
              // react-youtube renders <div><iframe/></div>; make both fill.
              ref={(el) => {
                if (el) {
                  const inner = el.querySelector("div");
                  if (inner) {
                    inner.style.width = "100%";
                    inner.style.height = "100%";
                  }
                }
              }}
            >
              <YouTube
                key={currentVideoId}
                ref={ytRef}
                videoId={currentVideoId}
                opts={{
                  width: "100%",
                  height: "100%",
                  playerVars: {
                    autoplay: 1,
                    mute: 1, // start muted; the audible effect unmutes
                    controls: 0,
                    modestbranding: 1,
                    rel: 0,
                    playsinline: 1,
                    enablejsapi: 1,
                    cc_load_policy: 0,
                  },
                }}
                style={{ width: "100%", height: "100%" }}
                onReady={(e) => {
                  try {
                    // Kill auto-captions — same pattern as VIP multiview.
                    e.target.unloadModule("captions");
                    e.target.unloadModule("cc");
                  } catch (err) {
                    /* noop */
                  }
                  try {
                    e.target.setVolume(volume);
                    if (audible) e.target.unMute();
                    else e.target.mute();
                  } catch (err) {
                    /* noop */
                  }
                }}
                onPlay={(e) => {
                  // Multiview kills captions from onPlay, not onReady —
                  // captions can load in after ready. Repeat here.
                  try {
                    e.target.unloadModule("captions");
                    e.target.unloadModule("cc");
                  } catch (err) {
                    /* noop */
                  }
                }}
                onEnd={() => handleEnded(currentVideoId)}
                onError={() => handleError(currentVideoId)}
              />
            </div>

            {/* Tap-capture layer — sits above the iframe so taps hit the
                tile, not the YouTube player. */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                background: "transparent",
              }}
            />

            {/* LIVE / PRE-RECORDED badge */}
            <div
              style={{
                position: "absolute",
                top: 8,
                left: 8,
                zIndex: 20,
                fontFamily: "'Lato', sans-serif",
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: 1,
                padding: "3px 8px",
                borderRadius: 5,
                color: "#fff",
                background: current.isLoop ? "#000" : "#e10600",
                boxShadow: "0 0 4px rgba(0,0,0,0.5)",
              }}
            >
              {current.isLoop ? "PRE-RECORDED" : "LIVE"}
            </div>

            {/* Streamer handle */}
            <div
              style={{
                position: "absolute",
                bottom: 8,
                left: 8,
                zIndex: 20,
                fontFamily: "'Lato', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                color: "#fff",
                background: "rgba(0,0,0,0.6)",
                padding: "3px 8px",
                borderRadius: 5,
              }}
            >
              @{current.streamer}
            </div>
          </>
        ) : (
          // Empty state — nothing live right now.
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: 16,
              fontFamily: "'Lato', sans-serif",
              color: "#fff",
            }}
          >
            <div style={{ fontSize: 17, fontWeight: 800 }}>No Live Streams</div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                opacity: 0.7,
                marginTop: 2,
              }}
            >
              Check Back Soon!
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default LiveStreamTile;
